import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import FormularioHubSpot from '../components/FormularioHubSpot';
import { C, SERIF, SANS, TRACK, MEDIDA, EASE, FOTO } from '../lib/tokens';
import { metasDeRuta, SEO_SITE } from '../lib/metas';
import { Imagen, precargarImagen } from '../components/Imagen';

const API = process.env.REACT_APP_BACKEND_URL;
/* Logo (manual 5.1): horizontal en barra de navegación, apilado en el pie.
   Negativa en marfil sobre hero/verde; positiva en verde sobre marfil y arena. */
const LOGO_NEG = '/brand/soha-horizontal-marfil.png';
const LOGO_POS = '/brand/soha-horizontal-verde.png';
const LOGO_APILADO = '/brand/soha-principal-verde.png';
const LOGO_APILADO_NEG = '/brand/soha-principal-marfil.png';
const COLLECTION_FILTER = FOTO;

/* ── English copy overlay (text only; images shared from CMS) ───────────── */
const EN_TEXT = {
  hero: {
    headline: 'A home that is already yours, in the right place.',
    support: 'Architect-designed co-ownership residences in the most singular destinations of Mexico and the United States.',
    cta_primary: 'See the collection',
    cta_secondary: 'Request private information',
  },
  manifesto: {
    lines: [
      'True luxury today is not owning more. It is time, space, and the people you love most — in the right place.',
      'SOHA Reserve exists for that: extraordinary homes, eight families per house, without the weight of full ownership.',
    ],
  },
  collection: {
    label: 'The Collection',
    heading: 'Few houses. Each one, unrepeatable.',
    items: [
      { sensory: 'The Pacific enters through every window.' },
      { sensory: 'Jungle, salt and silence at dawn.' },
      { sensory: 'The desert leans toward the sea.' },
      { sensory: 'Live oaks, open sky, southern calm.' },
      { sensory: 'The light of the Californian Pacific.' },
      { sensory: 'Snow, timber and an open fire.' },
    ],
  },
  principles: {
    label: 'Three convictions',
    items: [
      { title: 'Place', text: 'We build where almost no one else can. Place is the first luxury — and the hardest to replicate.' },
      { title: 'Authorship', text: 'Each house is signed by leading architecture studios. Never a replica, never a catalogue.' },
      { title: 'Community', text: 'A closed circle, curated by referral. Co-owners choose one another.' },
    ],
  },
  steps: {
    label: 'How it works',
    heading: 'Real ownership, without the usual burdens.',
    items: [
      { title: 'Conversation', text: 'A private meeting with the founding team. No sales pitch — only honest questions.' },
      { title: 'Visit', text: 'We walk the land together. We get to know the setting and the area, unhurried.' },
      { title: 'Reservation', text: 'Letter of intent and a deposit applied to the down payment, at your own pace.' },
      { title: 'Welcome', text: 'Review with your own advisors and a formal entry into the community.' },
    ],
  },
  trust: {
    label: 'Peace of mind',
    heading: 'Everything arranged so all you do is arrive.',
    items: [
      { title: 'Bank trust', text: 'Your property lives inside an administration trust at a banking institution. Real ownership, in your name.' },
      { title: 'Independent counsel', text: 'You review every contract with your own legal and tax advisors. No rush, no pressure.' },
      { title: 'In-house operation', text: 'SOHA operates each home end to end, to the standard of a five-star boutique hotel.' },
      { title: 'Construction warranty', text: 'Each residence is delivered with construction warranties and continuous maintenance.' },
    ],
  },
  testimonial: {
    quote: 'We bought a house. Without realizing it, we gained a different way of being with our family.',
    author: 'The G. Family',
    house: 'Co-owners · Chacalilla',
  },
  closing: {
    heading: 'You do not apply to enter.\nYou enter through conversation.',
    support: 'SOHA Reserve welcomes new families by invitation and referral. If you feel this is your place, tell us.',
    newsletter_heading: 'New houses, before anyone else.',
  },
  footer: {
    bottom_text: 'Private Collection · © MMXXVI · Private Information',
  },
};

const UI = {
  es: {
    access: 'Acceso copropietarios',
    langAlt: 'EN',
    formTitle: 'Solicitar una presentación privada',
    formIntro: 'Cuéntanos quién eres. Alguien del equipo te escribe personalmente en menos de 24 horas.',
    name: 'Nombre completo', email: 'Correo electrónico', phone: 'Teléfono',
    addDetails: 'Añadir detalles para recibir el dossier correcto',
    back: 'Volver', send: 'Enviar solicitud', sending: 'Enviando...',
    houseInterest: 'Casa o destino de interés', horizon: 'Horizonte de decisión', how: '¿Cómo nos conociste?',
    optional: 'Opcional', selectOne: 'Selecciona una opción',
    horizonNow: 'Ahora', horizon36: 'En 3 a 6 meses', horizonExplore: 'Explorando',
    successTitle: 'Gracias.', successBody: 'Recibimos tu solicitud. Alguien del equipo te escribirá personalmente muy pronto.',
    newsSub: 'Suscribirme', newsPlaceholder: 'Tu correo', newsOk: 'Listo. Serás de los primeros en saber.',
    errName: 'Por favor escribe tu nombre.', errEmail: 'Escribe un correo válido.',
  },
  en: {
    access: 'Owner access',
    langAlt: 'ES',
    formTitle: 'Request a private presentation',
    formIntro: 'Tell us who you are. Someone from the team will write to you personally within 24 hours.',
    name: 'Full name', email: 'Email', phone: 'Phone',
    addDetails: 'Add details to receive the right dossier',
    back: 'Back', send: 'Send request', sending: 'Sending...',
    houseInterest: 'House or destination of interest', horizon: 'Decision horizon', how: 'How did you hear about us?',
    optional: 'Optional', selectOne: 'Select an option',
    horizonNow: 'Now', horizon36: 'In 3 to 6 months', horizonExplore: 'Exploring',
    successTitle: 'Thank you.', successBody: 'We received your request. Someone from the team will write to you personally very soon.',
    newsSub: 'Subscribe', newsPlaceholder: 'Your email', newsOk: 'Done. You will be among the first to know.',
    errName: 'Please enter your name.', errEmail: 'Enter a valid email.',
  },
};

const DIAL_CODES = ['+52', '+1', '+34', '+54', '+57', '+56', '+51', '+55', '+593', '+58', '+44'];

const HEADER_H = 72;
const NAV = [
  { es: 'Nuestra colección', en: 'Our collection', id: 'coleccion' },
  { es: 'Cómo funciona', en: 'How it works', id: 'como-funciona' },
  { es: 'Autoría', en: 'Authorship', id: 'principios' },
  { es: 'Comunidad', en: 'Community', id: 'comunidad' },
  { es: 'Contacto', en: 'Contact', id: 'contacto' },
];

export const STATUS_LABEL = {
  disponible: { es: 'Disponible', en: 'Available' },
  ultimas: { es: 'Abierta a nuevas familias', en: 'Open to new families' },
  completa: { es: 'Completa', en: 'Fully owned' },
  estimado: { es: 'Estimado', en: 'Estimated' },
};

export function statusText(house, lang) {
  if (!house || !house.status) return '';
  if (house.status === 'estimado') {
    const y = house.estimated_year ? ` ${house.estimated_year}` : '';
    return `${lang === 'es' ? 'Estimado' : 'Estimated'}${y}`;
  }
  return (STATUS_LABEL[house.status] || {})[lang] || '';
}

export function setPageMeta({ title, description, image, url }) {
  if (title) document.title = title;
  const upsert = (key, keyType, content) => {
    if (!content) return;
    let el = document.head.querySelector(`meta[${keyType}="${key}"]`);
    if (!el) { el = document.createElement('meta'); el.setAttribute(keyType, key); document.head.appendChild(el); }
    el.setAttribute('content', content);
  };
  upsert('description', 'name', description);
  upsert('og:title', 'property', title);
  upsert('og:description', 'property', description);
  upsert('og:image', 'property', image);
  upsert('og:type', 'property', SEO_SITE.type);
  upsert('og:site_name', 'property', SEO_SITE.siteName);
  upsert('og:locale', 'property', SEO_SITE.locale);
  upsert('twitter:card', 'name', SEO_SITE.card);
  upsert('twitter:title', 'name', title);
  upsert('twitter:description', 'name', description);
  upsert('twitter:image', 'name', image);
  if (url) {
    upsert('og:url', 'property', url);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
    link.setAttribute('href', url);
  }
}

/* ── i18n deep-merge: overlay EN text onto CMS tree, keep images ────────── */
function mergeI18n(base, ov) {
  if (Array.isArray(ov)) return ov.map((o, i) => mergeI18n((base && base[i]) || {}, o));
  if (ov && typeof ov === 'object') {
    const out = { ...(base || {}) };
    for (const k in ov) out[k] = mergeI18n(base ? base[k] : undefined, ov[k]);
    return out;
  }
  return ov;
}

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - (HEADER_H + 24);
  window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
};

/* ── Reveal wrapper ─────────────────────────────────────────────────────── */
function Reveal({ children, delay = 0, y = 24, className = '', as = 'div' }) {
  const reduce = useReducedMotion();
  const M = motion[as] || motion.div;
  return (
    <M
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </M>
  );
}

const Label = ({ children }) => (
  <span style={{ fontFamily: SANS, letterSpacing: TRACK.eyebrow, color: C.gold }} className="text-[13px] uppercase">
    {children}
  </span>
);

export default function Landing({ showLogin }) {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const preview = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('preview') === '1';
  const [lang, setLang] = useState('es');
  const [content, setContent] = useState(null);
  const [loginOpen, setLoginOpen] = useState(!!showLogin);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBar, setShowBar] = useState(false);
  const [barDismissed, setBarDismissed] = useState(false);
  const [paginaColeccion, setPaginaColeccion] = useState(0);
  const [esMovil, setEsMovil] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);

  useEffect(() => { if (user && !preview) navigate('/dashboard'); }, [user, navigate, preview]);
  useEffect(() => { setLoginOpen(!!showLogin); }, [showLogin]);
  useEffect(() => {
    api.get('/landing/content').then((data) => {
      setContent(data);
      const tau = (data?.collection?.items || []).find((h) => h.slug === 'casa-tau');
      const heroImg = tau?.hero_image || tau?.image_url || data?.hero?.image_url;
      if (heroImg) precargarImagen(heroImg, '100vw');
    }).catch(() => setContent(null));
  }, []);
  useEffect(() => {
    const onScroll = () => {
      // En movil el hero es dividido: la barra pasa a marfil al salir de la foto.
      const umbral = window.innerWidth < 1024 ? 220 : window.innerHeight * 0.7;
      setScrolled(window.scrollY > umbral);
      setShowBar(window.scrollY > 400);
    };
    const onResize = () => setEsMovil(window.innerWidth < 1024);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  useEffect(() => {
    if (!content) return;
    setPageMeta(metasDeRuta('/'));
  }, [content, lang]);

  const t = UI[lang];
  // En movil la barra es siempre marfil (el hero dividido no lleva foto detras).
  const barraSolida = scrolled || esMovil;
  const navColor = barraSolida ? C.ink : C.linen;
  const c = content ? (lang === 'es' ? content : mergeI18n(content, EN_TEXT)) : null;

  // La coleccion muestra 6 casas por vista; el resto se recorre con el carrusel.
  const POR_VISTA = 6;
  const casas = c?.collection?.items || [];
  const totalPaginas = Math.max(1, Math.ceil(casas.length / POR_VISTA));
  const paginaActual = Math.min(paginaColeccion, totalPaginas - 1);
  const inicioVisible = paginaActual * POR_VISTA;
  const casasVisibles = casas.slice(inicioVisible, inicioVisible + POR_VISTA);

  // El hero de la home es la fotografia principal de Casa Tau (la casa fundadora).
  const casaTau = casas.find((h) => h.slug === 'casa-tau');
  const imagenHero = casaTau?.hero_image || casaTau?.image_url || c?.hero?.image_url;

  if (!c) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
        <img src={LOGO_POS} alt="SOHA Reserve" width="800" height="318" className="h-10 w-auto animate-pulse" style={{ filter: 'none' }} />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: SANS, background: C.bg, color: C.ink }} data-testid="public-home">
      {/* ═══ HEADER ═══ */}
      <header
        className="fixed top-0 inset-x-0 z-50 transition-all duration-500"
        style={{
          background: barraSolida ? C.linen92 : `linear-gradient(to bottom, ${C.scrim55} 0%, ${C.scrim28} 45%, ${C.scrim0} 100%)`,
          backdropFilter: barraSolida ? 'blur(12px)' : 'none',
          borderBottom: barraSolida ? `1px solid ${C.line}` : '1px solid transparent',
        }}
        data-testid="site-header"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 h-[72px] flex items-center justify-between gap-4">
          <button onClick={() => scrollTo('hero')} aria-label="SOHA Reserve" data-testid="header-logo" className="shrink-0">
            <img
              src={barraSolida ? LOGO_POS : LOGO_NEG} alt="SOHA Reserve" width="800" height="318"
              className="h-7 md:h-9 w-auto object-contain transition-opacity"
              style={{ filter: 'none', minWidth: 120, transitionDuration: 'var(--soha-dur)' }}
            />
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => scrollTo(n.id)}
                className="text-xs tracking-[0.16em] uppercase transition-opacity hover:opacity-60"
                style={{ color: navColor, fontFamily: SANS }}
                data-testid={`nav-${n.id}`}
              >
                {lang === 'es' ? n.es : n.en}
              </button>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-4 md:gap-5 shrink-0">
            <button
              onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
              className="text-xs tracking-[0.2em] transition-opacity hover:opacity-60 whitespace-nowrap"
              style={{ color: navColor, fontFamily: SANS }}
              data-testid="lang-toggle"
            >
              {t.langAlt}
            </button>
            <span className="hidden md:inline-block w-px h-3.5" style={{ background: barraSolida ? C.line : C.linen30 }} />
            <button
              onClick={() => setLoginOpen(true)}
              className="hidden md:inline text-[10px] tracking-[0.18em] uppercase transition-opacity opacity-70 hover:opacity-100 whitespace-nowrap"
              style={{ color: navColor, fontFamily: SANS }}
              data-testid="owner-access-link"
            >
              {t.access}
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              className="md:hidden -mr-1 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Menú"
              style={{ color: navColor }}
              data-testid="mobile-menu-open"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* ═══ MOBILE MENU OVERLAY ═══ */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[60] md:hidden flex flex-col"
            style={{ background: C.bg }}
            data-testid="mobile-menu"
          >
            <div className="h-[72px] px-6 flex items-center justify-between">
              <img src={LOGO_POS} alt="SOHA Reserve" width="800" height="318" className="h-7 w-auto object-contain" style={{ filter: 'none', minWidth: 120 }} />
              <button onClick={() => setMenuOpen(false)} className="p-2 -mr-2 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Cerrar" style={{ color: C.ink }} data-testid="mobile-menu-close">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-7">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  onClick={() => { setMenuOpen(false); setTimeout(() => scrollTo(n.id), 280); }}
                  className="text-left text-3xl"
                  style={{ fontFamily: SERIF, fontWeight: 300, color: C.ink }}
                  data-testid={`mobile-nav-${n.id}`}
                >
                  {lang === 'es' ? n.es : n.en}
                </button>
              ))}
            </nav>
            <div className="px-8 pb-10 flex items-center gap-8" style={{ borderTop: `1px solid ${C.line}` }}>
              <button onClick={() => setLang(lang === 'es' ? 'en' : 'es')} className="pt-6 text-xs tracking-[0.2em]" style={{ color: C.ink }} data-testid="mobile-lang-toggle">
                {lang === 'es' ? 'English' : 'Español'}
              </button>
              <button onClick={() => { setMenuOpen(false); setLoginOpen(true); }} className="pt-6 text-xs tracking-[0.18em] uppercase" style={{ color: C.accent }} data-testid="mobile-owner-access">
                {t.access}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ HERO ═══ */}
      <section id="hero" data-testid="hero-section">

      {/* Movil: hero dividido. La fotografia se ve en su perspectiva natural
          (4:3, sin recorte agresivo) y el titular baja a marfil, sin scrim. */}
      <div className="lg:hidden" data-testid="hero-movil">
        <div className="pt-[72px]" style={{ background: C.bg }} />
        <div className="relative w-full overflow-hidden md:max-h-[52vh]" style={{ aspectRatio: '3 / 2' }}>
          <Imagen
            src={imagenHero}
            alt=""
            ratio={3 / 2}
            sizes="100vw"
            prioridad
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{ filter: COLLECTION_FILTER }}
          />
        </div>
        <div className="px-6 md:px-12 pt-7 md:pt-10 pb-14 md:pb-16" style={{ background: C.bg }}>
          <motion.h1
            initial={reduce ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="text-4xl md:text-5xl leading-[1.08]"
            style={{ fontFamily: SERIF, fontWeight: 300, color: C.accent }}
            data-testid="hero-headline-movil"
          >
            {c.hero.headline}
          </motion.h1>
          <p className="mt-4 text-sm leading-relaxed" style={{ color: C.ink60, maxWidth: MEDIDA }}>
            {c.hero.support}
          </p>
          <button
            onClick={() => scrollTo('coleccion')}
            className="mt-7 w-full md:w-auto md:px-14 h-14 text-[13px] uppercase transition-colors"
            style={{
              background: C.accent, color: C.linen, fontFamily: SANS,
              letterSpacing: TRACK.caption, transitionDuration: 'var(--soha-dur)', transitionTimingFunction: EASE,
            }}
            data-testid="hero-cta-primary-movil"
          >
            {c.hero.cta_primary}
          </button>
        </div>
      </div>

      {/* Escritorio: hero a pantalla completa con parallax. */}
      <div ref={heroRef} className="relative h-screen overflow-hidden hidden lg:block" data-testid="hero-escritorio">
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <Imagen
            src={imagenHero}
            alt=""
            ratio={16 / 9}
            sizes="100vw"
            prioridad
            className="w-full h-full object-cover object-center"
            style={{ filter: COLLECTION_FILTER }}
          />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${C.scrim72} 0%, ${C.scrim55} 25%, ${C.scrim15} 58%, ${C.scrim25} 100%)` }} />
        </motion.div>

        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28">
          <motion.h1
            initial={reduce ? false : { opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.04]"
            style={{ fontFamily: SERIF, fontWeight: 300, color: C.linen }}
            data-testid="hero-headline"
          >
            {c.hero.headline}
          </motion.h1>
          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1 }}
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed"
            style={{ color: C.linen82, maxWidth: MEDIDA }}
          >
            {c.hero.support}
          </motion.p>
          <motion.div
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <button
              onClick={() => scrollTo('coleccion')}
              className="px-10 h-14 text-[13px] uppercase transition-colors"
              style={{
                background: C.accent, color: C.linen, fontFamily: SANS,
                letterSpacing: TRACK.caption, transitionDuration: 'var(--soha-dur)', transitionTimingFunction: EASE,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = C.accentDeep)}
              onMouseLeave={(e) => (e.currentTarget.style.background = C.accent)}
              data-testid="hero-cta-primary"
            >
              {c.hero.cta_primary}
            </button>
          </motion.div>
        </div>
      </div>
      </section>

      {/* ═══ MANIFIESTO ═══ */}
      <section className="py-20 md:py-32 px-6 md:px-12" data-testid="manifesto-section">
        <div className="max-w-4xl mx-auto">
          {c.manifesto.lines.map((line, i) => (
            <Reveal key={`mf-${i}`} delay={i * 0.1}>
              <p
                className="text-2xl md:text-4xl lg:text-[2.6rem] leading-[1.35] mb-8"
                style={{ fontFamily: SERIF, fontWeight: 300, color: i === 0 ? C.ink : C.ink60 }}
              >
                {line}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══ LA COLECCIÓN ═══ */}
      <section id="coleccion" className="py-20 md:py-32 px-6 md:px-12" style={{ background: C.bg2 }} data-testid="collection-section">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
              <div>
                <p style={{ color: C.accent }}><Label>{c.collection.label}</Label></p>
                <h2 className="mt-3 text-3xl md:text-5xl leading-tight max-w-xl" style={{ fontFamily: SERIF, fontWeight: 300 }}>
                  {c.collection.heading}
                </h2>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="collection-grid">
            {casasVisibles.map((h, i) => (
              <Reveal key={h.slug || `col-${paginaActual}-${i}`} delay={(i % 3) * 0.08}>
                <Link to={`/casa/${h.slug}`} className="group block" data-testid={`collection-card-${inicioVisible + i}`}>
                  <div className="relative overflow-hidden" style={{ aspectRatio: '3/4', background: C.bg2 }}>
                    {h.image_url ? (
                      <>
                        <Imagen
                          src={h.image_url} alt={h.name} ratio={3 / 4}
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-105"
                          style={{ filter: COLLECTION_FILTER }}
                        />
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2 px-4 text-center" style={{ background: C.bg2 }}>
                        <span className="text-2xl" style={{ fontFamily: SERIF, color: C.ink40 }}>{h.name}</span>
                        <span className="text-[10px] tracking-[0.24em] uppercase" style={{ color: C.ink40 }}>
                          {lang === 'es' ? 'Foto pendiente' : 'Photo pending'}
                        </span>
                      </div>
                    )}
                    {h.status && (
                      <span
                        className="absolute top-4 left-4 px-3 py-1 text-[10px] tracking-[0.18em] uppercase"
                        style={{ background: C.linen92, color: h.status === 'completa' ? C.ink40 : C.accent, letterSpacing: TRACK.caption }}
                        data-testid={`collection-status-${inicioVisible + i}`}
                      >
                        {statusText(h, lang)}
                      </span>
                    )}
                  </div>
                  <div className="mt-4 flex items-baseline justify-between gap-3">
                    <h3 className="text-2xl transition-opacity group-hover:opacity-70" style={{ fontFamily: SERIF, fontWeight: 400 }}>{h.name}</h3>
                    <span className="text-[11px] tracking-[0.16em] uppercase shrink-0" style={{ color: C.ink40 }}>{h.location}</span>
                  </div>
                  <p className="mt-1 text-[15px]" style={{ color: C.ink60 }}>{h.sensory}</p>
                </Link>
              </Reveal>
            ))}
          </div>

          {totalPaginas > 1 && (
            <div className="mt-12 flex items-center justify-between gap-6" data-testid="collection-carousel">
              <p className="text-[11px] tracking-[0.18em] uppercase" style={{ color: C.ink40, letterSpacing: TRACK.caption }} data-testid="collection-range">
                {inicioVisible + 1}–{inicioVisible + casasVisibles.length} {lang === 'es' ? 'de' : 'of'} {casas.length}
              </p>
              <div className="flex items-center gap-3">
                {Array.from({ length: totalPaginas }).map((_, p) => (
                  <button
                    key={`pt-${p}`}
                    onClick={() => setPaginaColeccion(p)}
                    aria-label={`${lang === 'es' ? 'Ver casas, grupo' : 'View homes, group'} ${p + 1}`}
                    aria-current={p === paginaActual}
                    className="h-[2px] transition-all"
                    style={{
                      width: p === paginaActual ? 32 : 16,
                      background: p === paginaActual ? C.accent : C.ink40,
                      transitionDuration: 'var(--soha-dur)', transitionTimingFunction: EASE,
                    }}
                    data-testid={`collection-dot-${p}`}
                  />
                ))}
                <button
                  onClick={() => setPaginaColeccion((p) => Math.max(0, p - 1))}
                  disabled={paginaActual === 0}
                  aria-label={lang === 'es' ? 'Casas anteriores' : 'Previous homes'}
                  className="h-11 w-11 flex items-center justify-center transition-colors disabled:opacity-30"
                  style={{ border: `1px solid ${C.ink40}`, color: C.ink }}
                  data-testid="collection-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setPaginaColeccion((p) => Math.min(totalPaginas - 1, p + 1))}
                  disabled={paginaActual >= totalPaginas - 1}
                  aria-label={lang === 'es' ? 'Mas casas' : 'More homes'}
                  className="h-11 w-11 flex items-center justify-center transition-colors disabled:opacity-30"
                  style={{ border: `1px solid ${C.ink40}`, color: C.ink }}
                  data-testid="collection-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ═══ PRINCIPIOS (alternating) ═══ */}
      <section id="principios" className="py-20 md:py-32 px-6 md:px-12" data-testid="principles-section">
        <div className="max-w-[1400px] mx-auto">
          <Reveal><p style={{ color: C.accent }} className="mb-12"><Label>{c.principles.label}</Label></p></Reveal>
          <div className="space-y-16 md:space-y-24">
            {c.principles.items.map((p, i) => {
              const flip = i % 2 === 1;
              return (
                <Reveal key={`pr-${i}`}>
                  <div className={`grid md:grid-cols-2 gap-10 md:gap-20 items-center ${flip ? 'md:[direction:rtl]' : ''}`}>
                    <div className="relative overflow-hidden md:[direction:ltr]" style={{ aspectRatio: '4/5' }}>
                      <Imagen src={p.image_url} alt={p.title} ratio={4 / 5}
                        sizes="(max-width: 768px) 100vw, 50vw"
                        className="w-full h-full object-cover" />
                    </div>
                    <div className="md:[direction:ltr]">
                      <p className="text-sm" style={{ color: C.gold, fontFamily: SERIF }}>{p.numeral}</p>
                      <h3 className="mt-2 text-4xl md:text-5xl" style={{ fontFamily: SERIF, fontWeight: 300 }}>{p.title}</h3>
                      <p className="mt-6 text-lg leading-relaxed max-w-md" style={{ color: C.ink60 }}>{p.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ CÓMO FUNCIONA ═══ */}
      <section id="como-funciona" className="py-20 md:py-32 px-6 md:px-12" style={{ background: C.bg2 }} data-testid="steps-section">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p style={{ color: C.accent }}><Label>{c.steps.label}</Label></p>
            <h2 className="mt-3 text-3xl md:text-5xl max-w-2xl leading-tight" style={{ fontFamily: SERIF, fontWeight: 300 }}>{c.steps.heading}</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-px" style={{ background: C.line }}>
            {c.steps.items.map((s, i) => (
              <Reveal key={`st-${i}`} delay={i * 0.08} className="h-full">
                <div className="h-full p-8" style={{ background: C.bg2 }}>
                  <p className="text-4xl md:text-5xl" style={{ fontFamily: SERIF, fontWeight: 300, color: C.gold }}>{s.numeral}</p>
                  <h3 className="mt-6 text-lg tracking-[0.14em] uppercase">{s.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed" style={{ color: C.ink60 }}>{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA INTERMEDIO ═══ */}
      <section className="py-16 md:py-24 px-6 md:px-12" data-testid="mid-cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="text-2xl md:text-3xl leading-snug" style={{ fontFamily: SERIF, fontWeight: 300 }}>
              {lang === 'es' ? 'La forma de conocer SOHA es una conversación.' : 'The way to know SOHA is a conversation.'}
            </p>
            <button
              onClick={() => scrollTo('contacto')}
              className="mt-6 inline-flex items-center min-h-[44px] text-sm tracking-[0.1em] underline underline-offset-[6px] hover:opacity-60 transition-opacity"
              style={{ color: C.accent }}
              data-testid="mid-cta-link"
            >
              {t.formTitle} &rarr;
            </button>
          </Reveal>
        </div>
      </section>

      {/* ═══ CONFIANZA ═══ */}
      <section id="confianza" className="py-20 md:py-32 px-6 md:px-12" data-testid="trust-section">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <p style={{ color: C.accent }}><Label>{c.trust.label}</Label></p>
            <h2 className="mt-3 text-3xl md:text-5xl max-w-2xl leading-tight" style={{ fontFamily: SERIF, fontWeight: 300 }}>{c.trust.heading}</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
            {c.trust.items.map((it, i) => (
              <Reveal key={`tr-${i}`} delay={(i % 2) * 0.08}>
                <div className="pt-8" style={{ borderTop: `1px solid ${C.line}` }}>
                  <h3 className="text-xl md:text-2xl" style={{ fontFamily: SERIF, fontWeight: 400 }}>{it.title}</h3>
                  <p className="mt-3 text-[15px] md:text-base leading-relaxed max-w-md" style={{ color: C.ink60 }}>{it.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIO ═══ */}
      <section className="py-20 md:py-32 px-6 md:px-12" style={{ background: C.bg2 }} data-testid="testimonial-section" id="comunidad">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            {c.testimonial.enabled && c.testimonial.quote && c.testimonial.author ? (
              <>
                <p className="text-3xl md:text-4xl lg:text-5xl leading-[1.3]" style={{ fontFamily: SERIF, fontWeight: 300, fontStyle: 'italic' }}>
                  &ldquo;{c.testimonial.quote}&rdquo;
                </p>
                <p className="mt-10 text-sm" style={{ color: C.ink }}>{c.testimonial.author}</p>
                <p className="mt-1 text-[11px] tracking-[0.22em] uppercase" style={{ color: C.accent }}>
                  {[c.testimonial.house, c.testimonial.city].filter(Boolean).join(' · ')}
                </p>
              </>
            ) : (
              <>
                <p style={{ color: C.accent }} className="mb-6"><Label>{lang === 'es' ? 'La Comunidad' : 'The Community'}</Label></p>
                <p className="text-3xl md:text-4xl lg:text-[2.6rem] leading-[1.3]" style={{ fontFamily: SERIF, fontWeight: 300 }}>
                  {c.testimonial.community_line}
                </p>
              </>
            )}
          </Reveal>
        </div>
      </section>

      {/* ═══ CIERRE + FORMULARIO ═══ */}
      <section id="contacto" className="py-20 md:py-32 px-6 md:px-12" style={{ background: C.accent, color: C.linen }} data-testid="closing-section">
        <div className="max-w-[1200px] mx-auto grid md:grid-cols-2 gap-16 lg:gap-24 items-start">
          <Reveal>
            <h2 className="text-3xl md:text-5xl leading-[1.15] whitespace-pre-line" style={{ fontFamily: SERIF, fontWeight: 300 }}>
              {c.closing.heading}
            </h2>
            <p className="mt-8 text-base leading-relaxed" style={{ color: C.linen65, maxWidth: MEDIDA }}>
              {c.closing.support}
            </p>
            <div className="mt-14 pt-10" style={{ borderTop: `1px solid ${C.linen15}` }}>
              <p className="text-xl md:text-2xl" style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 300 }}>{c.closing.newsletter_heading}</p>
              <NewsletterForm t={t} />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <FormularioHubSpot lang={lang} />
          </Reveal>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="pt-20 pb-10 px-6 md:px-12" style={{ background: C.bg2 }} data-testid="footer-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
            <div>
              <img src={LOGO_APILADO} alt="SOHA Reserve" width="800" height="318" className="h-16 w-auto object-contain mb-5" style={{ filter: 'none' }} />
              <p className="text-[13px] leading-relaxed max-w-xs" style={{ color: C.ink60 }}>{c.footer.address}</p>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.24em] uppercase mb-4" style={{ color: C.ink40 }}>{lang === 'es' ? 'Navegar' : 'Explore'}</p>
              <ul className="space-y-2.5">
                {NAV.map((n) => (
                  <li key={`f-${n.id}`}>
                    <button onClick={() => scrollTo(n.id)} className="text-sm hover:opacity-60 transition-opacity" style={{ color: C.ink }} data-testid={`footer-nav-${n.id}`}>
                      {lang === 'es' ? n.es : n.en}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.24em] uppercase mb-4" style={{ color: C.ink40 }}>{lang === 'es' ? 'Contacto' : 'Contact'}</p>
              <ul className="space-y-2.5 text-sm" style={{ color: C.ink }}>
                <li><a href={`mailto:${c.footer.contact_email}`} className="hover:opacity-60 transition-opacity">{c.footer.contact_email}</a></li>
                <li><a href={`tel:${(c.footer.contact_phone || '').replace(/\s/g, '')}`} className="hover:opacity-60 transition-opacity">{c.footer.contact_phone}</a></li>
                {c.footer.instagram && <li><a href={c.footer.instagram} target="_blank" rel="noreferrer" className="hover:opacity-60 transition-opacity">Instagram</a></li>}
                {c.footer.linkedin && <li><a href={c.footer.linkedin} target="_blank" rel="noreferrer" className="hover:opacity-60 transition-opacity">LinkedIn</a></li>}
              </ul>
            </div>
          </div>

          <p className="mt-14 text-[12px] leading-relaxed max-w-2xl" style={{ color: C.ink60 }}>{c.footer.legal_line}</p>

          <div className="mt-8 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" style={{ borderTop: `1px solid ${C.line}` }}>
            <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: C.ink40 }}>{c.footer.bottom_text}</p>
            <div className="flex items-center gap-6 text-[11px]" style={{ color: C.ink60 }}>
              <a href={c.footer.privacy_url || '#'} className="hover:opacity-60 transition-opacity">{lang === 'es' ? 'Aviso de privacidad' : 'Privacy'}</a>
              <a href={c.footer.terms_url || '#'} className="hover:opacity-60 transition-opacity">{lang === 'es' ? 'Términos' : 'Terms'}</a>
            </div>
          </div>
        </div>
      </footer>

      {/* ═══ FLOATING MOBILE CTA (dismissible, after scroll) ═══ */}
      <AnimatePresence>
        {showBar && !barDismissed && !menuOpen && (
          <motion.div
            initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden fixed bottom-0 inset-x-0 z-40 flex items-center"
            style={{ background: C.accent, color: C.linen }}
            data-testid="mobile-cta-bar"
          >
            <button onClick={() => scrollTo('contacto')} className="flex-1 py-4 text-sm tracking-[0.08em] min-h-[44px]" data-testid="mobile-cta-action">
              {t.formTitle}
            </button>
            <button onClick={() => setBarDismissed(true)} className="px-4 py-4 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Cerrar" style={{ color: C.linen65 }} data-testid="mobile-cta-close">
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <LoginModal open={loginOpen} onOpenChange={setLoginOpen} login={login} navigate={navigate} lang={lang} />

      <style>{`
        @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation: none !important; transition: none !important; scroll-behavior: auto !important; } }
        html { scroll-behavior: smooth; }
        section[id] { scroll-margin-top: 96px; }
        .soha-field:focus { border-color: var(--soha-marfil) !important; }
        .soha-field:focus-visible { outline: 2px solid var(--soha-dorado); outline-offset: 3px; }
      `}</style>
    </div>
  );
}

/* ── Newsletter ─────────────────────────────────────────────────────────── */
function NewsletterForm({ t }) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error(t.errEmail); return; }
    try { await api.post('/landing/subscribe', { email }); setDone(true); }
    catch { toast.error('Error'); }
  };
  if (done) return <p className="mt-4 text-sm" style={{ color: C.linen65 }}>{t.newsOk}</p>;
  return (
    <form onSubmit={submit} className="mt-5 flex items-center gap-4 max-w-md">
      <input
        type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.newsPlaceholder}
        className="flex-1 bg-transparent border-b py-2 text-sm focus:outline-none soha-field"
        style={{ borderColor: C.linen45, color: C.linen }}
        data-testid="newsletter-email"
      />
      <button type="submit" className="text-xs uppercase hover:opacity-60 transition-opacity" style={{ color: C.linen, letterSpacing: TRACK.caption }} data-testid="newsletter-submit">
        {t.newsSub} &rarr;
      </button>
    </form>
  );
}

/* ── Login / Register modal (owner access — same auth flow) ─────────────── */
function LoginModal({ open, onOpenChange, login, navigate, lang }) {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', name: '', invitation_key: '' });
  const [loading, setLoading] = useState(false);
  const isEs = lang === 'es';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      onOpenChange(false);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.detail || (isEs ? 'Credenciales incorrectas' : 'Invalid credentials'));
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, password: form.password, name: form.name, invitation_key: form.invitation_key }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Error'); }
      toast.success(isEs ? 'Cuenta creada. Inicia sesión.' : 'Account created. Sign in.');
      setMode('login');
    } catch (err) {
      toast.error(err.message || 'Error');
    } finally { setLoading(false); }
  };

  const inp = 'bg-transparent border-b border-white/20 text-[#F6F2E9] placeholder:text-white/35 rounded-none h-11 focus-visible:ring-0 focus-visible:border-[#C8A96A]';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm p-8 border-0 rounded-none" style={{ background: C.accent }} data-testid="login-modal">
        <div className="text-center mb-6">
          <img src={LOGO_APILADO_NEG} alt="SOHA Reserve" width="800" height="318" className="h-16 w-auto object-contain mx-auto mb-4" style={{ filter: 'none' }} />
          <DialogTitle asChild>
            <p className="text-[13px] uppercase font-normal" style={{ color: C.linen45, letterSpacing: TRACK.caption }}>
              {mode === 'login' ? (isEs ? 'Acceso Copropietarios' : 'Owner Access') : (isEs ? 'Activar Cuenta' : 'Activate Account')}
            </p>
          </DialogTitle>
        </div>
        {mode === 'login' ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <Input type="email" placeholder={isEs ? 'Correo electrónico' : 'Email'} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} data-testid="login-email" />
            <Input type="password" placeholder={isEs ? 'Contraseña' : 'Password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inp} data-testid="login-password" />
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-none tracking-wide" style={{ background: C.linen, color: C.accent, letterSpacing: TRACK.caption }} data-testid="login-submit">
              {loading ? (isEs ? 'Ingresando...' : 'Signing in...') : (isEs ? 'Ingresar' : 'Sign in')}
            </Button>
            <button type="button" onClick={() => setMode('register')} className="w-full text-center text-xs text-white/45 hover:text-white/75 transition-colors mt-2">
              {isEs ? 'Tengo una clave de invitación' : 'I have an invitation key'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="space-y-4">
            <Input placeholder={isEs ? 'Clave de invitación' : 'Invitation key'} value={form.invitation_key} onChange={(e) => setForm({ ...form, invitation_key: e.target.value })} className={`${inp} font-mono tracking-widest text-center`} data-testid="register-key" />
            <Input placeholder={isEs ? 'Nombre completo' : 'Full name'} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="register-name" />
            <Input type="email" placeholder={isEs ? 'Correo electrónico' : 'Email'} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} data-testid="register-email" />
            <Input type="password" placeholder={isEs ? 'Contraseña' : 'Password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inp} data-testid="register-password" />
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-none tracking-wide" style={{ background: C.linen, color: C.accent, letterSpacing: TRACK.caption }} data-testid="register-submit">
              {loading ? (isEs ? 'Registrando...' : 'Registering...') : (isEs ? 'Activar Cuenta' : 'Activate Account')}
            </Button>
            <button type="button" onClick={() => setMode('login')} className="w-full text-center text-xs text-white/45 hover:text-white/75 transition-colors mt-2">
              {isEs ? 'Ya tengo cuenta' : 'I already have an account'}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
